import { useState } from "react";
import { createUserWithEmailAndPassword, sendEmailVerification, signInWithPopup, updateProfile } from "firebase/auth";
import { Btn } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Logo } from "../components/Logo";
import { PW_REQS, pwStrength } from "../utils/validators";
import { auth, googleProvider } from "../services/firebase";
import { useResponsive } from "../hooks/useResponsive";

function AuthShell({ children, right }) {
  const { isMobile, isTablet } = useResponsive();
  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top, rgba(59,130,246,0.18), transparent 34%), linear-gradient(180deg, #07111f 0%, #0f172a 55%, #090d18 100%)",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        fontFamily: "'Space Grotesk','Space Mono',system-ui",
      }}
    >
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? 18 : isTablet ? 24 : 40 }}>
        <div style={{ width: "100%", maxWidth: isMobile ? "100%" : 460 }}>{children}</div>
      </div>
      {right && (
        <div
          style={{
            width: isMobile ? "100%" : 420,
            background: "linear-gradient(180deg, rgba(59,130,246,0.12), rgba(15,23,42,0.42))",
            borderLeft: isMobile ? "none" : "1px solid rgba(255,255,255,0.06)",
            borderTop: isMobile ? "1px solid rgba(255,255,255,0.06)" : "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ color: "#e2e8f0" }}>{right}</div>
        </div>
      )}
    </div>
  );
}

function SignUpBenefits() {
  const { isMobile } = useResponsive();
  const benefits = [
    "Encrypted uploads and secure file delivery",
    "Admin-managed storage pool with automatic allocation",
    "Google login, email verification, and modern sharing tools",
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, padding: isMobile ? "28px 20px" : "40px 32px" }}>
      <div style={{ fontSize: 42, fontWeight: 800, letterSpacing: "-0.04em" }}>HexVault</div>
      <p style={{ color: "#94a3b8", lineHeight: 1.7 }}>
        Built for production teams that need a premium vault experience without exposing the underlying storage providers.
      </p>
      {benefits.map((benefit) => (
        <div key={benefit} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span style={{ color: "#60a5fa", fontWeight: 700 }}>+</span>
          <span style={{ color: "#cbd5e1" }}>{benefit}</span>
        </div>
      ))}
    </div>
  );
}

const cardStyle = {
  background: "rgba(8,15,28,0.76)",
  border: "1px solid rgba(148,163,184,0.14)",
  borderRadius: 24,
  padding: 32,
  boxShadow: "0 24px 80px rgba(2,6,23,0.45)",
  backdropFilter: "blur(18px)",
  animation: "hexvaultFadeUp 450ms ease",
};

export function SignUp({ onNav }) {
  const { isMobile } = useResponsive();
  const [form, setForm] = useState({
    email: "",
    password: "",
    confirm: "",
    name: "",
    terms: false,
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [redirecting, setRedirecting] = useState(false);

  const set = (key) => (event) =>
    setForm((current) => ({
      ...current,
      [key]: event.target.type === "checkbox" ? event.target.checked : event.target.value,
    }));

  const strength = pwStrength(form.password);

  const validate = () => {
    const nextErrors = {};
    if (!form.email.match(/^[^@]+@[^@]+\.[^@]+$/)) nextErrors.email = "Enter a valid email address";
    if (!strength || ["Weak", "Fair"].includes(strength.label)) nextErrors.password = "Password must meet all requirements";
    if (form.password !== form.confirm) nextErrors.confirm = "Passwords do not match";
    if (!form.name.trim()) nextErrors.name = "Display name is required";
    if (!form.terms) nextErrors.terms = "You must accept the terms";
    return nextErrors;
  };

  const handleSubmit = async () => {
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      return;
    }

    setLoading(true);
    setRedirecting(false);
    setError("");
    try {
      const credential = await createUserWithEmailAndPassword(auth, form.email, form.password);
      await updateProfile(credential.user, { displayName: form.name });
      await sendEmailVerification(credential.user);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Signup failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setLoading(true);
    setRedirecting(true);
    setError("");
    try {
      googleProvider.setCustomParameters({ prompt: "select_account" });
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      setError(err.message || "Google sign up failed.");
      setLoading(false);
      setRedirecting(false);
    }
  };

  if (submitted) {
    return (
      <AuthShell>
        <div style={{ ...cardStyle, padding: isMobile ? 22 : 32, borderRadius: isMobile ? 20 : 24 }}>
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{ fontSize: 42, marginBottom: 16 }}>Inbox</div>
            <h2 style={{ fontSize: 24, fontWeight: 800, marginBottom: 8 }}>Check your inbox</h2>
            <p style={{ color: "#94a3b8", fontSize: 14, lineHeight: 1.7 }}>
              We sent a verification link to <strong style={{ color: "#f8fafc" }}>{form.email}</strong>.
              Verify the email, then sign in to continue.
            </p>
            <Btn style={{ marginTop: 24 }} onClick={() => onNav("signin")}>
              Continue to Sign In
            </Btn>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell right={<SignUpBenefits />}>
      <div style={{ ...cardStyle, padding: isMobile ? 22 : 32, borderRadius: isMobile ? 20 : 24 }}>
        <div style={{ marginBottom: 28 }}>
          <Logo onClick={() => onNav("landing")} />
          <h1 style={{ fontSize: 30, fontWeight: 800, marginBottom: 6, marginTop: 24, letterSpacing: "-0.04em", color: "#f8fafc" }}>
            Create your vault
          </h1>
          <p style={{ color: "#94a3b8", fontSize: 14 }}>5 GB free. No credit card required.</p>
        </div>
        {error && (
          <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8, padding: "10px 14px", color: "#fca5a5", fontSize: 13, marginBottom: 16, whiteSpace: "pre-wrap" }}>
            {error}
          </div>
        )}
        {redirecting && (
          <div style={{ background: "rgba(59,130,246,0.12)", border: "1px solid rgba(96,165,250,0.28)", borderRadius: 12, padding: "12px 14px", color: "#bfdbfe", fontSize: 13, marginBottom: 16 }}>
            Account verified. Redirecting into HexVault...
          </div>
        )}
        <Input label="Display name" placeholder="Your full name" value={form.name} onChange={set("name")} error={errors.name} />
        <Input label="Email" type="email" placeholder="you@example.com" value={form.email} onChange={set("email")} error={errors.email} />
        <div style={{ marginBottom: 18 }}>
          <Input label="Password" type="password" placeholder="Create a password" value={form.password} onChange={set("password")} error={errors.password} />
          {form.password && strength && (
            <div style={{ marginTop: -12 }}>
              <div style={{ height: 3, background: "rgba(255,255,255,0.08)", borderRadius: 99, overflow: "hidden", marginBottom: 8 }}>
                <div style={{ height: "100%", width: strength.width, background: strength.color, transition: "all 0.3s" }} />
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {PW_REQS.map((rule) => (
                  <span key={rule.label} style={{ fontSize: 11, color: rule.test(form.password) ? "#22c55e" : "#475569" }}>
                    {rule.test(form.password) ? "OK" : "NO"} {rule.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <Input label="Confirm password" type="password" placeholder="Re-enter password" value={form.confirm} onChange={set("confirm")} error={errors.confirm} />
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer", fontSize: 13, color: "#94a3b8" }}>
            <input type="checkbox" checked={form.terms} onChange={set("terms")} style={{ marginTop: 2 }} />
            <span>
              I agree to the <button type="button" style={linkButtonStyle}>Terms of Service</button> and{" "}
              <button type="button" style={linkButtonStyle}>Privacy Policy</button>
            </span>
          </label>
          {errors.terms && <div style={{ fontSize: 12, color: "#ef4444", marginTop: 4 }}>{errors.terms}</div>}
        </div>
        <Btn size="lg" onClick={handleSubmit} style={{ width: "100%", justifyContent: "center", marginBottom: 10 }} disabled={loading}>
          {loading ? "Creating account..." : redirecting ? "Redirecting..." : "Create Account"}
        </Btn>
        <Btn variant="ghost" size="lg" style={{ width: "100%", justifyContent: "center", border: "1px solid rgba(255,255,255,0.12)" }} onClick={handleGoogle}>
          Continue with Google
        </Btn>
        <p style={{ textAlign: "center", fontSize: 13, color: "#64748b", marginTop: 20 }}>
          Already have an account?{" "}
          <button onClick={() => onNav("signin")} style={linkButtonStyle}>
            Sign In
          </button>
        </p>
      </div>
    </AuthShell>
  );
}

const linkButtonStyle = {
  color: "#60a5fa",
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: 0,
};
